import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { numberWithCommas } from "../../utils";
import { SendResultSummary } from "./sendBatch";
import { AmountRow } from "./documentCells";

interface SuccessModalProps {
  open: boolean;
  /**
   * Counts of the send just made. null = the backend returned no result (older API): only a
   * plain success message can be shown.
   */
  result?: SendResultSummary | null;
  /** What was sent, as the failure table names it; the bill list's "บิล" by default. */
  documentNoun?: string;
  onClose: () => void;
}

/** The single dialog shown after a manual send that the backend accepted. */
export default function SuccessModal({
  open,
  result,
  documentNoun = "บิล",
  onClose,
}: SuccessModalProps) {
  const hasFailures = Boolean(result && result.failed_count > 0);
  const title = !result
    ? "ส่งเอกสาร E-Tax สำเร็จ!"
    : hasFailures
      ? "ส่งเอกสาร E-Tax แล้ว แต่มีบางใบไม่สำเร็จ"
      : "ส่งเอกสาร E-Tax สำเร็จ!";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} py={2}>
          {hasFailures ? (
            <WarningAmberIcon sx={{ fontSize: 56, color: "warning.main" }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
          )}
          <Typography variant="h6" fontWeight={700} textAlign="center">
            {title}
          </Typography>
        </Box>

        {result && (
          <Box>
            <AmountRow label="ส่งสำเร็จ" value={`${numberWithCommas(result.sent_count)} ใบ`} />
            <AmountRow
              label="ส่งไม่สำเร็จ"
              value={`${numberWithCommas(result.failed_count)} ใบ`}
            />
            <AmountRow
              label="ข้าม (ส่งไปแล้วก่อนหน้า)"
              value={`${numberWithCommas(result.already_sent_count)} ใบ`}
            />
            {result.skipped_test_count > 0 && (
              <AmountRow
                label={`ข้าม (${documentNoun}ทดสอบ)`}
                value={`${numberWithCommas(result.skipped_test_count)} ใบ`}
              />
            )}

            {result.failed.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  {documentNoun}ที่ส่งไม่สำเร็จ
                </Typography>
                <Box sx={{ maxHeight: 240, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>เลขที่{documentNoun}</TableCell>
                        <TableCell>สาเหตุ</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.failed.map((item) => (
                        <TableRow key={item.transaction_uuid}>
                          <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                            {item.transaction_no || item.transaction_uuid}
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-word" }}>
                            {item.message || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
        <Button variant="contained" onClick={onClose} sx={{ minWidth: 120 }}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}
